import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Crown,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  Zap,
  AlertTriangle,
} from "lucide-react";
import {
  getSubscriptionStatus,
  getSubscriptionDisplayStatus,
  isOnTrial,
  isSubscriptionExpired,
} from "../../utils/portalAccess";
import { fetchData } from "../../api/apiService";
import { USERS } from "../../api/endpointMap";

// Build the same subscription-status shape Dashboard.jsx writes to
// localStorage, but from a fresh /users/me response. Lifted here as a
// pure helper so the dropdown can hydrate itself in embed mode where
// Dashboard never mounts.
const _buildSubscriptionStatusFromUsersMe = (response) => {
  const org = response?.organization || {};
  // Partner-owned orgs carry NO plan (billed by the partner) — never on
  // trial, never expired. Without this branch the null plan fields fell
  // through to the "Free Trial" fallback in the badge.
  const isPartner =
    response?.subscription_status === "partner" || !!org.is_partner_org;
  const isExpired =
    !isPartner &&
    (response?.subscription_status === "expired" ||
      response?.subscription_status === "trial_expired");
  // Parse "YYYY-MM-DD" as LOCAL midnight (avoids the new Date("YYYY-MM-DD")
  // UTC-parse gotcha that produced fractional-day diffs in IST). For trials,
  // trial_end_date is the last active day (inclusive) → +1 in the diff so
  // today==end shows "1 day left". Math.round handles DST millisecond drift.
  const _parseLocal = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const trialEnd = _parseLocal(org.trial_end_date);
  const subEnd = _parseLocal(org.subscription_end_date);
  const endDate = org.is_trial ? trialEnd : subEnd;
  const todayMid = new Date();
  todayMid.setHours(0, 0, 0, 0);
  const daysRemaining = endDate
    ? Math.max(0, Math.round((endDate - todayMid) / 86400000) + 1)
    : 0;
  const ep = org.enabled_portals || {};
  const portalCategory = isPartner
    ? "Active"
    : org.is_trial ? "Trial" : org.subscription_category || "Active";
  const portals = [];
  if (ep.income_tax) portals.push({ name: "Income Tax", enabled: true, category: portalCategory });
  if (ep.gst)        portals.push({ name: "GST",        enabled: true, category: portalCategory });
  if (ep.tds)        portals.push({ name: "TDS",        enabled: true, category: portalCategory });
  return {
    status: response?.subscription_status,
    display_status: isPartner
      ? "Partner"
      : org.is_trial
      ? "Free Trial"
      : org.subscription_category || response?.subscription_status,
    is_trial: !isPartner && !!org.is_trial,
    is_admin: response?.role === "tenant_admin" || response?.role === "super_admin",
    is_expired: isExpired,
    trial_days_remaining: org.is_trial ? daysRemaining : 0,
    days_remaining: daysRemaining,
    trial_end_date: org.trial_end_date,
    subscription_end_date: org.subscription_end_date,
    subscription_category: org.subscription_category,
    plan_name: org.subscription_plan,
    portals,
  };
};

const SubscriptionDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const dropdownRef = useRef(null);

  // Always hydrate from a live /users/me call on mount. Reading
  // localStorage alone bites in embed mode where Dashboard.jsx never
  // mounts to populate `subscriptionStatus` — the badge then falls
  // back to the hardcoded "Free Trial" default (or to whatever stale
  // value a previous standalone session left behind), producing the
  // "two users in the same org see different plans" symptom.
  //
  // We seed state from localStorage first so the badge appears
  // immediately, then overwrite with the live fetch's authoritative
  // result. /users/me already returns org.is_trial /
  // subscription_category — both keyed off org.id, identical for
  // every user in the same org.
  useEffect(() => {
    setSubscriptionData(getSubscriptionStatus());
    let cancelled = false;
    fetchData(USERS.USER_INFO)
      .then((response) => {
        if (cancelled || !response?.subscription_status) return;
        const fresh = _buildSubscriptionStatusFromUsersMe(response);
        try {
          localStorage.setItem("subscriptionStatus", JSON.stringify(fresh));
        } catch {
          // localStorage quota / disabled — non-fatal. Other surfaces
          // (Dashboard) write it too; ours is best-effort hydration.
        }
        setSubscriptionData(fresh);
      })
      .catch(() => {
        // Network / 401. Leave the localStorage-seeded value in place.
      });
    return () => { cancelled = true; };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!subscriptionData) return null;

  const {
    status,
    display_status,
    is_trial,
    is_admin,
    is_expired,
    trial_days_remaining,
    days_remaining,
    subscription_end_date,
    trial_end_date,
    subscription_category,
    plan_name,
    portals,
  } = subscriptionData;

  // Category classification. The plan catalogue's PREMIUM tier (now "Pro";
  // "Advanced" was the old name) drives the purple/Crown treatment, and the
  // BASIC tier (now "Track"; old "Essential") the blue/Zap one. Centralised in
  // these two predicates so the Track/Pro rename touches one place, not the
  // seven inline string checks it replaced. Old names kept as aliases so any
  // pre-migration data still renders correctly.
  const isPremiumCategory = (cat) => cat === "Pro" || cat === "Advanced";
  const isBasicCategory = (cat) => cat === "Track" || cat === "Essential";

  // Determine badge color based on status
  const getBadgeStyles = () => {
    if (is_expired) {
      return "bg-red-100 text-red-700 border-red-200";
    }
    if (is_trial) {
      return "bg-amber-100 text-amber-700 border-amber-200";
    }
    if (isPremiumCategory(subscription_category)) {
      return "bg-purple-100 text-purple-700 border-purple-200";
    }
    if (isBasicCategory(subscription_category)) {
      return "bg-blue-100 text-blue-700 border-blue-200";
    }
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  // Get status icon
  const getStatusIcon = () => {
    if (is_expired) return <AlertTriangle size={14} />;
    if (is_trial) return <Clock size={14} />;
    if (isPremiumCategory(subscription_category)) return <Crown size={14} />;
    return <Zap size={14} />;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Get portal status color
  const getPortalStatusColor = (portal) => {
    if (!portal.enabled) return "bg-gray-100 text-gray-500";
    if (isPremiumCategory(portal.category)) return "bg-purple-100 text-purple-700";
    if (isBasicCategory(portal.category)) return "bg-blue-100 text-blue-700";
    if (portal.category === "Trial") return "bg-amber-100 text-amber-700";
    return "bg-gray-100 text-gray-600";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button — fixed h-8 so the navbar right-cluster lines
          up across SubscriptionDropdown / TrialCountdownPill / Upgrade
          / Usage / Logout at uniform 32px. */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 h-8 rounded-md border transition-all duration-200 hover:shadow-md ${getBadgeStyles()}`}
      >
        {getStatusIcon()}
        <span className="font-medium text-sm">
          {/* display_status is authoritative (it knows partner orgs =
              "Partner"); the old fallback chain wrongly showed "Free
              Trial" for partner orgs whose plan fields are null. */}
          {display_status ||
            (is_trial ? "Free Trial" : subscription_category || "Free Trial")}
        </span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden"
          >
            {/* Header */}
            <div
              className={`px-4 py-3 ${
                is_expired
                  ? "bg-red-50"
                  : is_trial
                  ? "bg-amber-50"
                  : isPremiumCategory(subscription_category)
                  ? "bg-purple-50"
                  : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Subscription Status
                  </p>
                  <p className="font-semibold text-gray-800 mt-0.5">
                    {display_status || status}
                  </p>
                </div>
                {!is_expired && (
                  <div
                    className={`p-2 rounded-full ${
                      is_trial
                        ? "bg-amber-100"
                        : isPremiumCategory(subscription_category)
                        ? "bg-purple-100"
                        : "bg-blue-100"
                    }`}
                  >
                    {is_trial ? (
                      <Clock size={20} className="text-amber-600" />
                    ) : isPremiumCategory(subscription_category) ? (
                      <Crown size={20} className="text-purple-600" />
                    ) : (
                      <Zap size={20} className="text-blue-600" />
                    )}
                  </div>
                )}
                {is_expired && (
                  <div className="p-2 rounded-full bg-red-100">
                    <AlertTriangle size={20} className="text-red-600" />
                  </div>
                )}
              </div>

              {/* Days remaining or expiry info. Trial countdown styled
                  prominently with escalating colour so the same urgency
                  cue that used to live on the navbar pill now lives here:
                  red ≤2 days, orange ≤7 days, amber otherwise. The
                  trial-end date is shown as a smaller subtitle so the
                  customer can plan around it without doing the math. */}
              {is_trial && !is_expired && trial_days_remaining > 0 && (() => {
                const tone =
                  trial_days_remaining <= 2
                    ? { bar: "bg-red-50 border-red-200", text: "text-red-700", sub: "text-red-500" }
                    : trial_days_remaining <= 7
                    ? { bar: "bg-orange-50 border-orange-200", text: "text-orange-700", sub: "text-orange-500" }
                    : { bar: "bg-amber-50 border-amber-200", text: "text-amber-700", sub: "text-amber-600" };
                return (
                  <div className={`mt-3 rounded-lg border px-3 py-2 ${tone.bar}`}>
                    <div className={`flex items-baseline gap-2 ${tone.text}`}>
                      <Clock size={16} />
                      <span className="text-2xl font-bold leading-none">
                        {trial_days_remaining}
                      </span>
                      <span className="text-sm font-medium">
                        day{trial_days_remaining === 1 ? "" : "s"} left
                      </span>
                    </div>
                    {trial_end_date && (
                      <p className={`mt-1 text-xs ${tone.sub}`}>
                        Trial ends {formatDate(trial_end_date)}
                      </p>
                    )}
                  </div>
                );
              })()}
              {!is_trial && !is_expired && days_remaining !== null && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-600">
                  <Calendar size={14} />
                  <span>
                    Valid until {formatDate(subscription_end_date)}
                  </span>
                </div>
              )}
              {is_expired && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-red-600">
                  <AlertTriangle size={14} />
                  <span>
                    {is_trial ? "Trial expired" : "Subscription expired"}
                  </span>
                </div>
              )}
            </div>

            {/* Portal Details */}
            <div className="px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                Module Status
              </p>
              <div className="space-y-2">
                {portals &&
                  portals.map((portal) => (
                    <div
                      key={portal.name}
                      className="flex items-center justify-between py-1.5"
                    >
                      <span className="text-sm text-gray-700">{portal.name}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPortalStatusColor(
                          portal
                        )}`}
                      >
                        {portal.enabled
                          ? portal.category || "Active"
                          : "Not Subscribed"}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Plan Details (if subscribed) */}
            {!is_trial && plan_name && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Plan</span>
                  <span className="text-sm font-medium text-gray-700">
                    {plan_name}
                  </span>
                </div>
              </div>
            )}

            {/* Footer with action */}
            {/*
            Commented out until payment gateway is ready
            <div className="px-4 py-3 border-t border-gray-100">
              <button className="w-full py-2 px-4 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                {is_trial || is_expired ? "Upgrade Now" : "Manage Subscription"}
              </button>
            </div>
            */}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionDropdown;
